// ---------------------------------------------------------------------------
// Partner portal for External API partners: /eps-login, /eps-account, /eps-listings,
// /eps-bookings. Asana 1219738994585570. Session handling: src/Util/epsPortal.js.
// Hub routes: VTHub /eps-portal/* (api/routes/epsPortal.js). Every call carries the
// portal session as a Bearer header and reads only the signed-in partner's own data.
// ---------------------------------------------------------------------------
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, useHistory, useLocation } from "react-router-dom";
import hubConstants from "../../Util/constants";
import {
	EPS_HOME, EPS_LOGIN, getEpsSession, setEpsSession, clearEpsSession, maskToken, codeFromSearch,
} from "../../Util/epsPortal";
import "../Auth/auth-redesign.css";
import "./eps-portal.css";

const HUB = hubConstants.SHUB_URL;

const call = async (path, { method = "GET", body, token } = {}) => {
	try {
		const res = await fetch(`${HUB}${path}`, {
			method,
			headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
			body: body ? JSON.stringify(body) : undefined,
		});
		let data = {};
		try { data = await res.json(); } catch (e) { data = {}; }
		return { status: res.status, data };
	} catch (e) {
		return { status: 0, data: { error: "We could not reach Villa Tracker. Check your connection and try again." } };
	}
};

/** A call made with the portal session; a refused session sends the partner back to sign in. */
const useEpsCall = () => {
	const history = useHistory();
	return useCallback(async (path, opts = {}) => {
		const session = getEpsSession();
		if (!session) { history.replace(EPS_LOGIN); return { status: 401, data: {} }; }
		const r = await call(path, { ...opts, token: session.token });
		if (r.status === 401) { clearEpsSession(); history.replace(EPS_LOGIN); }
		return r;
	}, [history]);
};

const copy = async (text) => {
	try { await navigator.clipboard.writeText(text); return true; } catch (e) { return false; }
};

// ---------------------------------------------------------------------------
// Sign in
// ---------------------------------------------------------------------------
export function EpsLogin() {
	const history = useHistory();
	const location = useLocation();
	const [email, setEmail] = useState("");
	const [busy, setBusy] = useState(false);
	const [sent, setSent] = useState("");
	const [error, setError] = useState("");
	const code = codeFromSearch(location.search);

	useEffect(() => {
		if (!code) return;
		let cancelled = false;
		(async () => {
			setBusy(true);
			const r = await call("/eps-portal/session/redeem", { method: "POST", body: { code } });
			const ok = r.status === 200 && r.data && r.data.success && r.data.token;
			// The code is used up by now: keep the session even if this page was left meanwhile.
			if (ok) setEpsSession(r.data);
			if (cancelled) return;
			setBusy(false);
			if (ok) {
				history.replace(EPS_HOME);
			} else {
				setError((r.data && r.data.error) || "This sign-in link did not work. Ask for a new one.");
				history.replace(EPS_LOGIN);
			}
		})();
		return () => { cancelled = true; };
	}, [code, history]);

	const submit = async (e) => {
		e.preventDefault();
		setError(""); setSent("");
		if (!email.trim()) { setError("Enter the email address of your API partner account."); return; }
		setBusy(true);
		const r = await call("/eps-portal/session/link", { method: "POST", body: { email: email.trim() } });
		setBusy(false);
		if (r.status === 200 && r.data && r.data.success) setSent(r.data.message || "Check your inbox for the sign-in link.");
		else setError((r.data && r.data.error) || "Something went wrong. Please try again.");
	};

	return (
		<div className="auth-split">
			<div className="auth-left">
				<div className="auth-left-mid">
					<h1 className="auth-headline">Your API partner account.</h1>
					<p className="auth-sub">See your API credentials, the villas shared with you and your booking requests.</p>
				</div>
				<div className="auth-left-footer">Villa Tracker by Smiling House</div>
				<span className="auth-circle c1" />
				<span className="auth-circle c2" />
			</div>
			<div className="auth-right">
				<form className="auth-form" onSubmit={submit}>
					<div className="auth-eyebrow">Villa Tracker partner portal</div>
					<h2 className="auth-title">Sign in with an email link</h2>
					<p className="auth-desc">Enter the email address of your API partner account. We send you a link that signs you in; there is no password.</p>
					{code && busy && <div className="eps-note">Signing you in…</div>}
					{error && <div className="auth-error">{error}</div>}
					{sent && <div className="eps-note eps-note-ok">{sent}</div>}
					<label className="auth-label" htmlFor="eps-email">E-Mail</label>
					<input
						id="eps-email"
						className="auth-input"
						type="email"
						value={email}
						onChange={(e) => setEmail(e.target.value)}
						placeholder="you@company.com"
						autoComplete="email"
					/>
					<button type="submit" className="auth-btn" disabled={busy}>Email me a sign-in link</button>
					<p className="auth-cta">Villa owner or property manager? <span onClick={() => history.push("/login")}>Sign in here</span></p>
				</form>
			</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------
function EpsLayout({ title, children }) {
	const history = useHistory();
	const session = getEpsSession() || {};
	const signOut = () => { clearEpsSession(); history.replace(EPS_LOGIN); };
	return (
		<div className="eps-portal">
			<header className="eps-header">
				<div className="eps-brand">Villa Tracker <span>Partner portal</span></div>
				<nav className="eps-nav">
					<NavLink to="/eps-account" activeClassName="active">Account</NavLink>
					<NavLink to="/eps-listings" activeClassName="active">Villas</NavLink>
					<NavLink to="/eps-bookings" activeClassName="active">Bookings</NavLink>
				</nav>
				<div className="eps-user">
					<span>{session.partnerName || ""}</span>
					<button type="button" className="eps-btn eps-btn-light" onClick={signOut}>Sign out</button>
				</div>
			</header>
			<main className="eps-main">
				<h1 className="eps-title">{title}</h1>
				{children}
			</main>
		</div>
	);
}

const Loading = () => <div className="eps-note">Loading…</div>;
const Failed = ({ text }) => <div className="auth-error">{text}</div>;

// ---------------------------------------------------------------------------
// Account
// ---------------------------------------------------------------------------
export function EpsAccount() {
	const epsCall = useEpsCall();
	const [account, setAccount] = useState(null);
	const [error, setError] = useState("");
	const [showToken, setShowToken] = useState(false);
	const [copied, setCopied] = useState("");

	useEffect(() => {
		(async () => {
			const r = await epsCall("/eps-portal/me");
			if (r.status === 200 && r.data && r.data.account) setAccount(r.data.account);
			else if (r.status !== 401) setError((r.data && r.data.error) || "Could not load your account.");
		})();
	}, [epsCall]);

	const doCopy = async (what, text) => { if (await copy(text)) { setCopied(what); setTimeout(() => setCopied(""), 2000); } };

	return (
		<EpsLayout title="Your account">
			{error && <Failed text={error} />}
			{!account && !error && <Loading />}
			{account && (
				<>
					<div className="eps-badges">
						{account.sandbox
							? <span className="eps-badge eps-badge-test">Sandbox: test villas{account.paymentTestMode ? ", test payments (no real charge)" : ""}</span>
							: <span className="eps-badge eps-badge-live">Live account</span>}
						{account.requireHostedPayment && <span className="eps-badge">Guests pay on our payment page</span>}
					</div>
					<table className="eps-table eps-kv">
						<tbody>
							<tr><th>Company</th><td>{account.partnerName}</td></tr>
							<tr><th>Account email</th><td>{account.email}</td></tr>
							<tr><th>API base URL</th><td><code>{account.apiBaseUrl}</code></td></tr>
							<tr>
								<th>Partner ID</th>
								<td><code>{account.partnerId}</code>{" "}
									<button type="button" className="eps-btn eps-btn-light" onClick={() => doCopy("id", account.partnerId)}>{copied === "id" ? "Copied" : "Copy"}</button>
								</td>
							</tr>
							<tr>
								<th>Bearer token</th>
								<td><code>{showToken ? account.bearerToken : maskToken(account.bearerToken)}</code>{" "}
									<button type="button" className="eps-btn eps-btn-light" onClick={() => setShowToken(!showToken)}>{showToken ? "Hide" : "Show"}</button>{" "}
									<button type="button" className="eps-btn eps-btn-light" onClick={() => doCopy("token", account.bearerToken)}>{copied === "token" ? "Copied" : "Copy"}</button>
								</td>
							</tr>
							<tr><th>Villas shared with you</th><td>{account.shared} ({account.connected} connected)</td></tr>
							<tr><th>Documentation</th><td><a href={account.docsUrl} target="_blank" rel="noreferrer">{account.docsUrl.replace(/^https:\/\//, "")}</a></td></tr>
						</tbody>
					</table>
					<p className="eps-small">Keep the token private: anyone who has it can use your account. If it is ever exposed, tell us and we issue a new one.</p>
				</>
			)}
		</EpsLayout>
	);
}

// ---------------------------------------------------------------------------
// Shared villas
// ---------------------------------------------------------------------------
const STATUS_LABEL = { connected: "Connected", disconnected: "Disconnected", pending: "Pending" };

export function EpsListings() {
	const epsCall = useEpsCall();
	const [rows, setRows] = useState(null);
	const [error, setError] = useState("");
	const [filter, setFilter] = useState("");
	const [busyId, setBusyId] = useState("");

	useEffect(() => {
		(async () => {
			const r = await epsCall("/eps-portal/listings");
			if (r.status === 200 && r.data && Array.isArray(r.data.listings)) setRows(r.data.listings);
			else if (r.status !== 401) setError((r.data && r.data.error) || "Could not load your villas.");
		})();
	}, [epsCall]);

	const setStatus = async (id, status) => {
		setBusyId(id); setError("");
		const r = await epsCall(`/eps-portal/listings/${encodeURIComponent(id)}/status`, { method: "POST", body: { status } });
		setBusyId("");
		if (r.status === 200 && r.data && r.data.success) setRows((prev) => prev.map((x) => (x.id === id ? { ...x, status } : x)));
		else if (r.status !== 401) setError((r.data && r.data.error) || "Could not change this villa.");
	};

	const shown = useMemo(() => {
		const q = filter.trim().toLowerCase();
		return (rows || []).filter((x) => !q || [x.id, x.title, x.city, x.country].some((v) => String(v || "").toLowerCase().includes(q)));
	}, [rows, filter]);
	const connected = (rows || []).filter((x) => x.status === "connected").length;

	return (
		<EpsLayout title="Villas shared with you">
			<p className="eps-small">Connected villas come through the API (listings, calendars, quotes and bookings). Disconnect a villa to leave it out of your site. To add villas or destinations, ask your account manager.</p>
			{error && <Failed text={error} />}
			{!rows && !error && <Loading />}
			{rows && (
				<>
					<div className="eps-toolbar">
						<input className="auth-input eps-filter" placeholder="Search by villa, town or country" value={filter} onChange={(e) => setFilter(e.target.value)} />
						<span>{rows.length} shared, {connected} connected{filter ? `, ${shown.length} shown` : ""}</span>
					</div>
					{rows.length === 0 && <div className="eps-note">No villas are shared with your account yet.</div>}
					{rows.length > 0 && (
						<table className="eps-table">
							<thead><tr><th>Villa ID</th><th>Name</th><th>Town</th><th>Country</th><th>Status</th><th /></tr></thead>
							<tbody>
								{shown.map((x) => (
									<tr key={x.id}>
										<td><code>{x.id}</code></td>
										<td>{x.title || (x.found ? "" : "(no longer available)")}</td>
										<td>{x.city}</td>
										<td>{x.country}</td>
										<td><span className={`eps-status eps-status-${x.status}`}>{STATUS_LABEL[x.status] || x.status}</span></td>
										<td>
											{x.status === "connected"
												? <button type="button" className="eps-btn eps-btn-light" disabled={busyId === x.id} onClick={() => setStatus(x.id, "disconnected")}>Disconnect</button>
												: <button type="button" className="eps-btn" disabled={busyId === x.id || !x.found} onClick={() => setStatus(x.id, "connected")}>Connect</button>}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					)}
				</>
			)}
		</EpsLayout>
	);
}

// ---------------------------------------------------------------------------
// Bookings
// ---------------------------------------------------------------------------
const day = (v) => (v ? String(v).slice(0, 10) : "");
const money = (amount, currency) => (amount === null || amount === undefined || amount === "" ? "" : `${Number(amount).toLocaleString(undefined, { maximumFractionDigits: 2 })} ${currency || ""}`.trim());

export function EpsBookings() {
	const epsCall = useEpsCall();
	const [rows, setRows] = useState(null);
	const [error, setError] = useState("");

	useEffect(() => {
		(async () => {
			const r = await epsCall("/eps-portal/bookings");
			if (r.status === 200 && r.data && Array.isArray(r.data.bookings)) setRows(r.data.bookings);
			else if (r.status !== 401) setError((r.data && r.data.error) || "Could not load your bookings.");
		})();
	}, [epsCall]);

	return (
		<EpsLayout title="Your booking requests">
			{error && <Failed text={error} />}
			{!rows && !error && <Loading />}
			{rows && rows.length === 0 && <div className="eps-note">No booking requests yet.</div>}
			{rows && rows.length > 0 && (
				<table className="eps-table">
					<thead><tr><th>Sent</th><th>Villa</th><th>Check-in</th><th>Check-out</th><th>Guests</th><th>Guest</th><th>Your reference</th><th>Total</th><th>Payment</th><th>Status</th><th>Confirmation</th></tr></thead>
					<tbody>
						{rows.map((x) => (
							<tr key={`${x.kind}-${x.id}`}>
								<td>{day(x.createdAt)}</td>
								<td><code>{x.listingId}</code>{x.listingName ? <div className="eps-small">{x.listingName}</div> : null}</td>
								<td>{day(x.checkIn)}</td>
								<td>{day(x.checkOut)}</td>
								<td>{x.guests ?? ""}</td>
								<td>{x.guestName}</td>
								<td>{x.partnerReference}</td>
								<td>{money(x.total, x.currency)}</td>
								<td>{x.payment || ""}</td>
								<td>{x.status}</td>
								<td>{x.confirmationCode}</td>
							</tr>
						))}
					</tbody>
				</table>
			)}
		</EpsLayout>
	);
}
